import { List, ListItemButton, ListItemText, ListSubheader } from '@mui/material';
import classNames from 'classnames';
import { memo, PropsWithChildren } from 'react';
import { GMCheckbox } from '../Checkbox';
import css from './List.module.scss';

interface ListProps extends PropsWithChildren {
    /**
     * Removes the list borders
     */
    noBorders?: boolean;
}

export const GMList: React.FC<ListProps> = memo((props) => {
    return (
        <List
            component={'nav'}
            className={classNames(css.list, {
                [css.border]: !props.noBorders,
            })}
        >
            {props.children}
        </List>
    );
});

interface ListItemProps extends PropsWithChildren {
    /**
     * Callback when list item is clicked
     */
    onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
    /**
     * Whether the list item has been selected. Will highlight the list item
     * if not using checkboxes
     */
    selected?: boolean;
    /**
     * Whether the list item is disabled
     */
    disabled?: boolean;
    /**
     * Whether the hover and click behavior is disabled
     * without disabling child components
     */
    interactionDisabled?: boolean;
    /**
     * Whether a checkbox should display to indicate selected status.
     * Will disable the list item highlighting when selected
     */
    includeCheckbox?: boolean;
    /**
     * Reduces top and bottom padding
     */
    thin?: boolean;
}

export const GMListItem: React.FC<ListItemProps> = memo((props) => {
    return (
        <ListItemButton
            disabled={props.disabled}
            disableTouchRipple={props.interactionDisabled}
            className={classNames(
                css.listItem, {
                    [css.disabled]: props.interactionDisabled,
                    [css.highlight]: props.selected && !props.includeCheckbox,
                    [css.hasCheckbox]: props.includeCheckbox,
                    [css.thin]: !!props.thin,
                },
            )}
            onClick={props.onClick}
        >
            {props.includeCheckbox ? <GMCheckbox checked={!!props.selected} /> : null}
            <ListItemText className={css.listItemText}>
                {props.children}
            </ListItemText>
        </ListItemButton>
    );
});

interface ListSubheaderProps extends PropsWithChildren {
    /**
     * Callback when sub header is clicked
     */
    onClick?: (event: React.MouseEvent<HTMLLIElement>) => void;
}

export const GMListSubHeader: React.FC<ListSubheaderProps> = memo((props) => {
    return (
        <ListSubheader className={classNames(css.subheader, props.onClick ? css.clickable : '')} onClick={props.onClick}>
            {props.children}
        </ListSubheader>
    );
});